import { observer } from 'mobx-react-lite'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { ReactElement, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { useBannerPromotionStore } from '../../../store/promotion/BannerPromotionStore'
import giftIcon from '../../../assets/images/giftIcon.png'
import { I18n, LanguageContext } from 'common'
import { localizePromoImageUrl } from '../../promotion/localizePromoImageUrl'
import { defaultTheme } from '../../../theme'
import { zIndex } from '../../../theme/zIndex'
import { breakpoints } from '../../../theme/breakpoints'
import { PromoType } from '../../../api/promotion/interface'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { findNumberOfEntries } from 'cart'
import {
  getItemsTotalCost,
  getVehiclePartsCoreCost,
} from 'libs/cart/src/store/cartSelectors'

export const BannerPromoDrawer = observer((): ReactElement => {
  const { selectedLanguage } = useContext(LanguageContext)
  const {
    promotionData,
    fetchPromotionData,
    showBottomBannerPromotion,
    entryData,
    setEntryData,
    handleBannerPromoDrawerOpenExpanded,
    expandPromoDrawer,
    handleCloseBannerPromoDrawer,
    matchCoverage,
    promotionCoverageData,
  } = useBannerPromotionStore()
  // const cart = useShoppingCart()
  const totalPartsCostInCart = useSelector((state: RootState): number => {
    let allCartsGrandTotal = 0
    state.cart.cartData.vehicles.forEach((cartVehicle) => {
      allCartsGrandTotal += getItemsTotalCost(state, cartVehicle.vehicle)
    })

    return allCartsGrandTotal
  })

  const totalCoreCostInCart = useSelector((state: RootState): number => {
    let allCartsGrandTotal = 0
    state.cart.cartData.vehicles.forEach((cartVehicle) => {
      allCartsGrandTotal += getVehiclePartsCoreCost(state, cartVehicle.vehicle)
    })

    return allCartsGrandTotal
  })

  // const numberOfEntries = cart?.findNumberOfEntries(
  //   promotionCoverageData?.coverage || ''
  // )
  const numberOfEntries = useSelector((state: RootState) =>
    findNumberOfEntries(
      promotionCoverageData?.coverage || '',
      state.cart.cartData.vehicles
    )
  )
  const minimumValueForGetAnEntry = promotionData[0]?.minAmt

  const firstPromotionData = promotionData[0]

  const totalEntries = totalPartsCostInCart / minimumValueForGetAnEntry
  const totalEntriesWinned = Math.trunc(totalEntries)
  const decimalValue = totalEntries - totalEntriesWinned
  const remaningEntryValue = Math.abs(
    decimalValue * minimumValueForGetAnEntry - minimumValueForGetAnEntry
  ).toFixed(2)
  const maxEntries = promotionData[0]?.maxEntries
  const promoType = promotionData[0]?.promoType

  useEffect(() => {
    fetchPromotionData()
  }, [fetchPromotionData])

  const handleExpand = () => {
    if (!expandPromoDrawer) {
      handleBannerPromoDrawerOpenExpanded()
    }
    if (expandPromoDrawer) {
      handleCloseBannerPromoDrawer()
    }
  }

  useEffect(() => {
    if (showBottomBannerPromotion && totalPartsCostInCart) {
      setEntryData({
        totalEntriesWinned,
        remaningEntryValue,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalCoreCostInCart,
    totalPartsCostInCart,
    showBottomBannerPromotion,
    expandPromoDrawer,
  ])

  if (!showBottomBannerPromotion || !firstPromotionData) {
    return null
  }

  const promotionLink = firstPromotionData?.promotionLinks[0]?.url
  const leftImageSrc = localizePromoImageUrl(
    firstPromotionData.leftImage,
    selectedLanguage
  )

  const handleFallbackImage = () => {
    const promoLogo = document.getElementById('promo-logo')
    if (!promoLogo) {
      return
    }
    if (
      firstPromotionData.leftImage &&
      promoLogo.getAttribute('src') !== firstPromotionData.leftImage
    ) {
      promoLogo.setAttribute('src', firstPromotionData.leftImage)
      return
    }
    promoLogo.setAttribute('src', giftIcon)
    promoLogo.style.width = '47.67'
    promoLogo.style.height = '43.49'
  }

  return (
    <Container>
      {/* TODO: The height of `ExpandedBanner` elements is not responsive. It is hardcoded 
      for both desktop and mobile version. When we have lengthy promotion message from API then 
      `ExpandedBanner` can't expand and hence we will loose come content as the message will be overflowed. So 
      we should use auto height for `ExpandedBanner` element. But this inturn will have implication for 
      `GiveAwayChevron`. Because the bottom-position of `GiveAwayChevron` is fixed based on the height of the 
      `ExpandedBanner`.
       */}
      <GiveAwayChevron
        offset={expandPromoDrawer.toString()}
        onClick={handleExpand}
      >
        <p>{firstPromotionData?.promoName}</p>
        {!expandPromoDrawer ? <ExpandMore /> : <ExpandLess />}
      </GiveAwayChevron>
      {expandPromoDrawer && (
        <ExpandedBanner>
          <LogoContainer>
            {' '}
            <img
              src={leftImageSrc}
              alt="promo logo"
              onError={handleFallbackImage}
              id="promo-logo"
              height="43.49"
            />
          </LogoContainer>
          {promoType === PromoType.AmountDriven &&
            totalPartsCostInCart === 0 && (
              <DetailsContainer>
                <Add>
                  <I18n name="add" /> ${firstPromotionData?.minAmt}{' '}
                  <I18n name="onThisOrderForAnAutomaticEntry" />
                </Add>
                <Details>
                  <I18n name="click" />{' '}
                  <span>
                    <a
                      rel="noreferrer"
                      target="_blank"
                      href={promotionData[0]?.promotionLinks[0]?.url}
                    >
                      <I18n name="here" />
                    </a>
                  </span>{' '}
                  <I18n name="forFullSweepstakesDetails" />
                </Details>
              </DetailsContainer>
            )}
          {promoType === PromoType.AmountDriven && totalPartsCostInCart > 0 && (
            <DetailsContainer>
              {' '}
              {totalEntriesWinned > 0 &&
              maxEntries > 0 &&
              totalEntriesWinned >= maxEntries ? (
                <Add>
                  <I18n name="youHaveReachedTheMaximumNumberOfEntries" />
                </Add>
              ) : (
                <Add>
                  <I18n name="addJust" /> ${entryData?.remaningEntryValue}{' '}
                  <I18n name="onThisOrderFor" />{' '}
                  {totalEntriesWinned === 0 ? 'an ' : 'another '}
                  <I18n name="automaticEntry" />
                </Add>
              )}
              <Details>
                <I18n name="click" />{' '}
                <span>
                  <a rel="noreferrer" target="_blank" href={promotionLink}>
                    <I18n name="here" />
                  </a>
                </span>{' '}
                <I18n name="forFullSweepstakesDetails" />
              </Details>
            </DetailsContainer>
          )}
          {promoType === PromoType.BrandDriven && (
            <DetailsContainer>
              {matchCoverage ? (
                <Add>
                  <I18n name="congratulationsThisOrderQualifiesFor" />{' '}
                  {numberOfEntries}{' '}
                  {numberOfEntries > 0 ? 'entries.' : 'entry.'}
                </Add>
              ) : (
                <Add>
                  <I18n name="addA" /> {promotionData[0]?.promoName}{' '}
                  <I18n name="productToThisOrderForAnAutomaticEntry" />
                </Add>
              )}

              {promotionData[0]?.promotionLinks[0]?.url && (
                <Details>
                  <I18n name="click" />{' '}
                  <span>
                    <a
                      rel="noreferrer"
                      target="_blank"
                      href={promotionData[0]?.promotionLinks[0]?.url}
                    >
                      <I18n name="here" />
                    </a>
                  </span>{' '}
                  <I18n name="forFullSweepstakesDetails" />
                </Details>
              )}
            </DetailsContainer>
          )}
        </ExpandedBanner>
      )}
    </Container>
  )
})

const Container = styled.div``

const ExpandedBanner = styled.div`
  position: fixed;
  bottom: 0px;
  left: 0px;
  display: flex;
  gap: 10px;
  padding: 12px;
  width: 100%;
  height: 65.65px;
  background: ${defaultTheme.colors.primary};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  font-family: Roboto;
  animation: fadeInAnimation ease 1s;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  z-index: ${zIndex.stickyHeader};

  @keyframes fadeInAnimation {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    height: 92px;
  }
`

const LogoContainer = styled.div`
  width: 57px;
`

const DetailsContainer = styled.div``

const Add = styled.p`
  font-family: Roboto;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  margin: 0px;
`

const Details = styled.p`
  font-family: Roboto;
  font-size: 12px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  margin: 0px;
  a {
    font-weight: bold;
    color: black;
  }
`

interface GiveAwayChevronProps {
  offset: string
}

const GiveAwayChevron = styled.div<GiveAwayChevronProps>`
  background-color: ${defaultTheme.colors.primary};
  position: fixed;
  bottom: ${(props) => (props.offset === 'true' ? '62px' : '0px')};
  left: 11px;
  height: 34px;
  background: ${defaultTheme.colors.primary};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  border-radius: 3px 3px 0px 0px;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
  display: flex;
  padding: 5px 13px 5px 12px;
  cursor: pointer;

  p {
    margin: 0px 6px 0px 0px;
  }
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    bottom: ${(props) => (props.offset === 'true' ? '89px' : '0px')};
    align-items: center;
    height: 48px;
  }
`
